/**
 * Audit logs 列表页（E2 验收页）。
 *
 * Phase 1-5 / Plan §4.1 / Design §16 E2.
 *
 * 功能：
 *  - 过滤栏：actorId / resourceType / action / method / requestId / 时间范围
 *    （资源类型、动作可手输，也可从带中文的候选里选；Method 含 GET = 读留痕）
 *  - 表格列：时间 / actor / role / method / 动作 / path / status / resource / requestId
 *  - 读留痕（GET）：顾问看客户内容、后台拿到客户 / 合伙人手机号 / 地址 / unionId 明文，
 *    只记「谁、何时、看了谁」，不记内容；动作与资源类型显示中文（见 ./labels.ts）
 *  - 点击 requestId → 弹窗显示同 requestId 的全部日志（跨服务追溯）
 *  - 点击行 → 详情抽屉显示 before/after summary
 *  - 分页默认 20/页
 *
 * 权限：用 PermissionGate 包裹，没 audit_log:read 直接 fallback 提示。
 *
 * 注意：UI 隐藏不是安全边界 —— 真正权限校验在 admin-service 端 requirePermission。
 */
import { useState, useMemo } from 'react';
import {
  Alert,
  AutoComplete,
  Table,
  Form,
  Input,
  Button,
  Space,
  Tag,
  DatePicker,
  Select,
  Drawer,
  Modal,
  Descriptions,
  Empty,
  Result,
  Card,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listAuditLogs,
  getAuditLogsByRequestId,
  type AuditLogDto,
  type ListAuditLogsFilter,
} from '../../api/audit-logs';
import {
  ACTION_OPTIONS,
  RESOURCE_TYPE_OPTIONS,
  actionLabel,
  filterLabelOption,
  isReadAuditAction,
  resourceTypeLabel,
  withRaw,
} from './labels';

const { RangePicker } = DatePicker;
const PAGE_SIZE_DEFAULT = 20;

interface FilterFormValues {
  actorId?: string;
  resourceType?: string;
  action?: string;
  method?: string;
  requestId?: string;
  range?: [unknown, unknown];
}

function statusColor(code: number): string {
  if (code >= 500) return 'red';
  if (code >= 400) return 'orange';
  if (code >= 300) return 'gold';
  return 'green';
}

function methodColor(method: string): string {
  switch (method) {
    case 'POST': return 'blue';
    case 'PATCH':
    case 'PUT': return 'purple';
    case 'DELETE': return 'red';
    case 'GET': return 'cyan'; // 读留痕
    default: return 'default';
  }
}

function AuditLogsPageInner() {
  const [form] = Form.useForm<FilterFormValues>();
  const [filters, setFilters] = useState<ListAuditLogsFilter>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  // 详情抽屉
  const [detailRow, setDetailRow] = useState<AuditLogDto | null>(null);
  // by-request 弹窗
  const [traceRequestId, setTraceRequestId] = useState<string | null>(null);

  const queryParams: ListAuditLogsFilter = useMemo(
    () => ({
      ...filters,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [filters, page, pageSize],
  );

  const listQuery = useQuery({
    queryKey: ['audit-logs', queryParams],
    queryFn: () => listAuditLogs(queryParams),
  });

  const traceQuery = useQuery({
    enabled: !!traceRequestId,
    queryKey: ['audit-logs', 'by-request', traceRequestId],
    queryFn: () => getAuditLogsByRequestId(traceRequestId!),
  });

  function applyFilters() {
    const v = form.getFieldsValue();
    const next: ListAuditLogsFilter = {
      actorId: v.actorId,
      resourceType: v.resourceType,
      action: v.action,
      method: v.method,
      requestId: v.requestId,
    };
    if (v.range && Array.isArray(v.range)) {
      const [start, end] = v.range;
      if (start && typeof (start as { toISOString?: () => string }).toISOString === 'function') {
        next.startAt = (start as { toISOString: () => string }).toISOString();
      }
      if (end && typeof (end as { toISOString?: () => string }).toISOString === 'function') {
        next.endAt = (end as { toISOString: () => string }).toISOString();
      }
    }
    setFilters(next);
    setPage(1);
  }

  function resetFilters() {
    form.resetFields();
    setFilters({});
    setPage(1);
  }

  const columns: ColumnsType<AuditLogDto> = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作者',
      dataIndex: 'actorId',
      key: 'actorId',
      width: 140,
      ellipsis: true,
    },
    {
      title: '角色',
      dataIndex: 'actorRole',
      key: 'actorRole',
      width: 110,
      render: (r: string) => <Tag>{r}</Tag>,
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      width: 90,
      render: (m: string) => <Tag color={methodColor(m)}>{m}</Tag>,
    },
    {
      title: '动作',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      render: (a: string) => {
        const label = actionLabel(a);
        // 读留痕着色成醒目的橙红；写动作（create / update …）保持素色显示原值
        return (
          <Tooltip title={label ? a : undefined}>
            <Tag color={isReadAuditAction(a) ? 'volcano' : undefined}>{label ?? a}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '路径',
      dataIndex: 'path',
      key: 'path',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'statusCode',
      key: 'statusCode',
      width: 90,
      render: (c: number) => <Tag color={statusColor(c)}>{c}</Tag>,
    },
    {
      title: '资源',
      key: 'resource',
      width: 180,
      ellipsis: true,
      render: (_: unknown, row) => (
        <span title={row.resourceType + (row.resourceId ? ' / ' + row.resourceId : '')}>
          {resourceTypeLabel(row.resourceType) ?? row.resourceType}
          {row.resourceId ? ' / ' + row.resourceId : ''}
        </span>
      ),
    },
    {
      title: 'Request ID',
      dataIndex: 'requestId',
      key: 'requestId',
      width: 180,
      ellipsis: true,
      render: (rid: string) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setTraceRequestId(rid);
          }}
        >
          {rid}
        </Button>
      ),
    },
  ];

  return (
    <Card title="审计日志（E2）" data-testid="audit-logs-page">
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
        data-testid="audit-logs-filter-form"
      >
        <Form.Item name="actorId" label="操作者">
          <Input placeholder="admin id" allowClear style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="resourceType" label="资源类型">
          <AutoComplete
            placeholder="admin / order / refund …"
            allowClear
            style={{ width: 200 }}
            options={RESOURCE_TYPE_OPTIONS}
            filterOption={filterLabelOption}
            data-testid="audit-logs-resource-type"
          />
        </Form.Item>
        <Form.Item name="action" label="动作">
          <AutoComplete
            placeholder="create / update / delete …"
            allowClear
            style={{ width: 220 }}
            options={ACTION_OPTIONS}
            filterOption={filterLabelOption}
            data-testid="audit-logs-action"
          />
        </Form.Item>
        <Form.Item name="method" label="Method">
          <Select
            placeholder="任意"
            allowClear
            style={{ width: 120 }}
            options={[
              { value: 'GET', label: 'GET（读留痕）' },
              { value: 'POST', label: 'POST' },
              { value: 'PATCH', label: 'PATCH' },
              { value: 'PUT', label: 'PUT' },
              { value: 'DELETE', label: 'DELETE' },
            ]}
          />
        </Form.Item>
        <Form.Item name="requestId" label="Request ID">
          <Input placeholder="跨服务追溯" allowClear style={{ width: 220 }} />
        </Form.Item>
        <Form.Item name="range" label="时间范围">
          <RangePicker showTime />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={applyFilters} data-testid="audit-logs-search">
              查询
            </Button>
            <Button onClick={resetFilters} data-testid="audit-logs-reset">
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table<AuditLogDto>
        rowKey="id"
        columns={columns}
        dataSource={listQuery.data?.items ?? []}
        loading={listQuery.isLoading}
        onRow={(row) => ({
          onClick: () => setDetailRow(row),
        })}
        pagination={{
          current: page,
          pageSize,
          total: listQuery.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showTotal: (total) => `共 ${total} 条`,
        }}
        locale={{ emptyText: listQuery.isError ? '加载失败' : <Empty /> }}
      />

      {/* 详情抽屉 */}
      <Drawer
        title="审计日志详情"
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        width={640}
        data-testid="audit-logs-detail-drawer"
      >
        {detailRow && isReadAuditAction(detailRow.action) && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="这是一条读留痕：只记录谁、何时查看了谁，不记录查看到的内容。后台查看用户 / 订单 / 合伙人 / 工单明文时，同一操作者对同一对象 10 分钟内只记一条。"
            data-testid="audit-logs-read-hint"
          />
        )}
        {detailRow && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{detailRow.id}</Descriptions.Item>
            <Descriptions.Item label="时间">
              {new Date(detailRow.createdAt).toLocaleString('zh-CN')}
            </Descriptions.Item>
            <Descriptions.Item label="操作者">{detailRow.actorId}</Descriptions.Item>
            <Descriptions.Item label="角色">{detailRow.actorRole}</Descriptions.Item>
            <Descriptions.Item label="Method">{detailRow.method}</Descriptions.Item>
            <Descriptions.Item label="Path">{detailRow.path}</Descriptions.Item>
            <Descriptions.Item label="Status">{detailRow.statusCode}</Descriptions.Item>
            <Descriptions.Item label="资源类型">
              {withRaw(detailRow.resourceType, resourceTypeLabel(detailRow.resourceType))}
            </Descriptions.Item>
            <Descriptions.Item label="资源 ID">{detailRow.resourceId ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="动作">
              {withRaw(detailRow.action, actionLabel(detailRow.action))}
            </Descriptions.Item>
            <Descriptions.Item label="Request ID">{detailRow.requestId}</Descriptions.Item>
            <Descriptions.Item label="Idempotency Key">
              {detailRow.idempotencyKey ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="IP">{detailRow.ip ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="User Agent">{detailRow.userAgent ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Before">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {detailRow.beforeSummary ?? '-'}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="After">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {detailRow.afterSummary ?? '-'}
              </pre>
            </Descriptions.Item>
            {detailRow.errorMessage && (
              <Descriptions.Item label="Error">
                <pre style={{ margin: 0, color: '#cf1322', whiteSpace: 'pre-wrap' }}>
                  {detailRow.errorMessage}
                </pre>
              </Descriptions.Item>
            )}
            {detailRow.crossServiceActor && (
              <Descriptions.Item label="跨服务调用方">
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {detailRow.crossServiceActor}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>

      {/* by-request 弹窗：同 requestId 跨服务追溯 */}
      <Modal
        title={`同 Request ID 的全部日志: ${traceRequestId ?? ''}`}
        open={traceRequestId !== null}
        onCancel={() => setTraceRequestId(null)}
        footer={null}
        width={900}
        data-testid="audit-logs-trace-modal"
      >
        <Table<AuditLogDto>
          rowKey="id"
          size="small"
          columns={columns.filter((c) => c.key !== 'requestId')}
          dataSource={traceQuery.data?.items ?? []}
          loading={traceQuery.isLoading}
          pagination={false}
        />
      </Modal>
    </Card>
  );
}

export default function AuditLogsPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.AUDIT_LOG_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="审计日志页面仅 super_admin 可见，请联系超管申请权限。"
        />
      }
    >
      <AuditLogsPageInner />
    </PermissionGate>
  );
}
